/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Name resolution for the audit detail renderers.
 *
 * Audit diffs carry only raw identifiers — `user:sql46367`, `entity:asset:sql291901`,
 * `entity:monitoring-point:sql70029`, `geofence:sql97957` — which mean nothing to a reader. These
 * hooks resolve them to Fresh names through endpoints api-insights already exposes, so the panel
 * shows "Phantom D1" rather than "#70029".
 *
 * Resolution is best-effort throughout: every caller falls back to the id, because an audit record
 * that renders with raw ids is still useful and one that fails to render is not. Two cases resolve
 * to nothing by design — a geofence that has since been deleted or hidden is filtered out by the
 * geofences endpoint, and an id from another company is out of scope — and both degrade to the id.
 */

import { useQueries, useQuery } from '@tanstack/react-query'

import { useApi } from '@/hooks'

interface FreshUser {
  userId: number
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  email?: string | null
}

interface NamedRecord {
  id: number
  name: string
}

const FIVE_MINUTES = 5 * 60_000

/** A Fresh user's display name, or `undefined` while loading / unresolved. */
export const useUserName = (freshUserId?: string) => {
  const { api } = useApi()

  return useQuery({
    queryKey: ['audit-fresh-user', freshUserId],
    queryFn: () =>
      api.simpleGet<{ data: FreshUser[] }>({
        path: `/fresh/users?${new URLSearchParams({ userId: freshUserId ?? '' }).toString()}`,
        origin: 'auditLogs',
      }),
    enabled: !!freshUserId,
    staleTime: FIVE_MINUTES,
    select: (res): string | undefined => {
      const user = res.data?.[0]
      if (!user) return undefined
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
      return name || user.username || user.email || undefined
    },
  })
}

interface FreshRole {
  id: string
  name: string
}

/**
 * A map of privilege id → role name, lowercased on both sides.
 *
 * A user-administration record stores privileges as bare role GUIDs, which are the same values as
 * Fresh's `aspnet_Roles.RoleId`. bonito writes them uppercase and SQL Server returns them in its
 * own casing, so neither side of the lookup can be trusted to match the other verbatim — every key
 * is lowercased here and callers must lowercase before reading.
 *
 * The ids are sorted and de-duplicated so that two records naming the same privileges in a
 * different order share one cache entry.
 */
export const useRoleNames = (roleIds: string[]) => {
  const { api } = useApi()
  const ids = Array.from(new Set(roleIds.map((id) => id.toLowerCase()))).sort()
  const key = ids.join(',')

  return useQuery({
    queryKey: ['audit-fresh-roles', key],
    queryFn: () =>
      api.simpleGet<{ data: FreshRole[] }>({
        path: `/fresh/roles?${new URLSearchParams({ id: key }).toString()}`,
        origin: 'auditLogs',
      }),
    enabled: ids.length > 0,
    staleTime: FIVE_MINUTES,
    select: (res): Map<string, string> => new Map((res.data ?? []).map((role) => [role.id.toLowerCase(), role.name])),
  })
}

const toNameMap = (res: { data?: NamedRecord[] }): Map<string, string> =>
  new Map((res.data ?? []).map((row) => [String(row.id), row.name]))

const useNamesAcrossCompanies = <Row extends NamedRecord>(
  key: string,
  endpoint: string,
  companyIds: (number | undefined)[],
  ids: string[],
  label: (row: Row) => string
) => {
  const { api } = useApi()
  const joined = ids.join(',')
  const companies = Array.from(new Set(companyIds.filter((id): id is number => !!id))).sort((a, b) => a - b)

  return useQueries({
    queries: companies.map((companyId) => ({
      queryKey: [key, companyId, joined],
      queryFn: () =>
        api.simpleGet<{ data: Row[] }>({
          path: `${endpoint}?${new URLSearchParams({ companyId: String(companyId), id: joined, take: '1000' }).toString()}`,
          origin: 'auditLogs',
        }),
      enabled: ids.length > 0,
      staleTime: FIVE_MINUTES,
      retry: false,
    })),
    combine: (results) => ({
      isLoading: results.some((result) => result.isLoading),
      data: new Map(
        results.flatMap((result) => (result.data?.data ?? []).map((row) => [String(row.id), label(row)] as const))
      ),
    }),
  })
}

/** A map of Fresh asset id → name, looked up in each of the given companies. */
export const useAssetNames = (companyIds: (number | undefined)[], assetIds: string[]) =>
  useNamesAcrossCompanies<NamedRecord>('audit-fresh-assets', '/fresh/assets', companyIds, assetIds, (row) => row.name)

/**
 * A map of Fresh monitoring point id → label, `<asset> - <point>` as the configuration screen
 * shows it ("KK_Asset_1 - TempMP1"), since a point's own name is rarely unique across a fleet.
 *
 * Deliberately does not pass `deleted=false`: a door alert whose monitoring point was later removed
 * still needs to say which one it was.
 */
export const useMonitoringPointNames = (companyIds: (number | undefined)[], pointIds: string[]) =>
  useNamesAcrossCompanies<NamedRecord & { assetName?: string }>(
    'audit-fresh-monitoring-points',
    '/fresh/monitoring-points',
    companyIds,
    pointIds,
    (row) => (row.assetName ? `${row.assetName} - ${row.name}` : row.name)
  )

/**
 * A map of geofence id → name, and one of geofence category id → name.
 *
 * Both are fetched for the whole company rather than by id: neither endpoint filters by id, and an
 * alert configuration references few enough of them that one cached company-wide list is cheaper
 * than the alternative. `withDescendants` matches the dropdowns the alert screen itself uses, so a
 * geofence selectable there resolves here.
 */
export const useGeofenceNames = (companyId: number, enabled: boolean) => {
  const { api } = useApi()

  return useQuery({
    queryKey: ['audit-fresh-geofences', companyId],
    queryFn: () =>
      api.simpleGet<{ data: NamedRecord[] }>({
        path: `/fresh/geofences?${new URLSearchParams({ companyIds: String(companyId), skip: '0', take: '10000', withDescendants: 'true' }).toString()}`,
        origin: 'auditLogs',
      }),
    enabled: enabled && !!companyId,
    staleTime: FIVE_MINUTES,
    select: toNameMap,
  })
}

interface FreshAlarmCode {
  fridgeMake: string
  code: number
  description: string
}

/**
 * The fridge-alarm catalogue, keyed `<FridgeMake>:<code>` — e.g. `T:100` → "Heater Fan Failure (CR)".
 *
 * Fetched whole rather than by id: the catalogue is ~909 rows, is the same for every company, and
 * never changes in practice, so one long-lived cache entry serves every alert configuration a user
 * opens. A fridge-alarm alert commonly enables hundreds of codes at once, which makes fetching by id
 * the more expensive option, not the cheaper one.
 *
 * `enabled` is passed by the caller so the request is only made for records that actually reference
 * alarms — most alert configurations do not.
 */
export const useFridgeAlarmDescriptions = (enabled: boolean) => {
  const { api } = useApi()

  return useQuery({
    queryKey: ['audit-fresh-fridge-alarm-codes'],
    queryFn: () =>
      api.simpleGet<{ data: FreshAlarmCode[] }>({
        path: '/fresh/fridge-alarm-codes?skip=0&take=5000',
        origin: 'auditLogs',
      }),
    enabled,
    // The catalogue is effectively static; re-fetching it per panel would be pure waste.
    staleTime: Infinity,
    select: (res): Map<string, string> =>
      new Map((res.data ?? []).map((row) => [`${row.fridgeMake}:${String(row.code)}`, row.description])),
  })
}

export const useGeofenceCategoryNames = (companyId: number, enabled: boolean) => {
  const { api } = useApi()

  return useQuery({
    queryKey: ['audit-fresh-geofence-categories', companyId],
    queryFn: () =>
      api.simpleGet<{ data: NamedRecord[] }>({
        path: `/fresh/geofence-categories?${new URLSearchParams({ companyIds: String(companyId), skip: '0', take: '10000', withDescendants: 'true' }).toString()}`,
        origin: 'auditLogs',
      }),
    enabled: enabled && !!companyId,
    staleTime: FIVE_MINUTES,
    select: toNameMap,
  })
}

interface SmartGroup {
  id: number
  name: string
  deleted: boolean
}

/**
 * A map of smart group id → its name and whether it has since been deleted, for one company.
 *
 * Fetched whole per company and cached for the session, the way the company list is: a company
 * has few groups, and every record naming one of them then resolves from the same cached list
 * rather than issuing a request of its own. The API checks the caller may see the company.
 */
export const useSmartGroups = (companyId: number | undefined) => {
  const { api } = useApi()

  return useQuery({
    queryKey: ['audit-smart-groups', companyId],
    queryFn: () =>
      api.simpleGet<{ data: SmartGroup[] }>({
        path: `/smart-groups?${new URLSearchParams({ companyId: String(companyId) }).toString()}`,
        origin: 'auditLogs',
      }),
    enabled: !!companyId,
    staleTime: Infinity,
    select: (res): Map<string, SmartGroup> => new Map((res.data ?? []).map((group) => [String(group.id), group])),
  })
}
